import { useEffect, useState } from 'react';
import type { EnvironmentPoolStats, GatewayStats, LlmGatewayStats } from '@reef/schema';
import { api, type PlatformSummary } from '../api.ts';

/**
 * The platform the factory stands on, on one page.
 *
 * One page rather than six, and that is the argument rather than a layout
 * preference: the six pieces only mean something together. Six separate
 * dashboards let somebody conclude the context graph is healthy while nothing
 * has leased a warm environment in a week — and the claim being made here is
 * precisely that without the platform underneath, the factory is a set of
 * scripts that break the first time a repository moves.
 *
 * Every tile is measured rather than configured. "Four pools configured" is a
 * number that stays green through an outage; "eleven leases served" is not.
 */
export function PlatformView() {
  const [platform, setPlatform] = useState<PlatformSummary | null>(null);
  const [output, setOutput] = useState<{
    agentAuthoredPullRequests: number;
    automatedMigrations: number;
  } | null>(null);
  const [gateway, setGateway] = useState<GatewayStats | null>(null);
  const [llm, setLlm] = useState<{ stats: LlmGatewayStats; budgetMs: number; within: boolean } | null>(
    null,
  );
  const [pools, setPools] = useState<EnvironmentPoolStats[]>([]);
  const [windowHours, setWindowHours] = useState(24 * 7);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;

    Promise.all([
      api.factoryPlatform(windowHours),
      api.mcpGateway(),
      api.llmGateway(windowHours),
      api.environments(windowHours),
    ])
      .then(([summary, mcp, llmGateway, environments]) => {
        if (!current) return;
        setPlatform(summary.platform);
        setOutput(summary.output);
        setGateway(mcp.gateway);
        setLlm({
          stats: llmGateway.gateway,
          budgetMs: llmGateway.guardBudgetMs,
          within: llmGateway.withinBudget,
        });
        setPools(environments.pools);
      })
      .catch((caught) => current && setError((caught as Error).message));

    return () => {
      current = false;
    };
  }, [windowHours]);

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Platform</span>
        </span>
        <span className="spacer" />
        <label className="studio-window">
          Window
          <select
            value={windowHours}
            onChange={(event) => setWindowHours(Number(event.target.value))}
            aria-label="Platform window"
          >
            <option value={24}>24 hours</option>
            <option value={24 * 7}>7 days</option>
            <option value={24 * 30}>30 days</option>
          </select>
        </label>
      </header>

      <div className="studio-column wide">
        {error && <div className="banner danger">{error}</div>}

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>What the factory produced</h2>
          </div>
          <div className="studio-card-body">
            <div className="tiles">
              <Tile
                label="Agent-authored PRs"
                value={output ? String(output.agentAuthoredPullRequests) : '—'}
                note="completed factory runs in the window"
              />
              <Tile
                label="Automated migrations"
                value={output ? String(output.automatedMigrations) : '—'}
                note="maintenance targets landed, all time"
              />
            </div>
            {/*
              A count rather than a share, deliberately. Reef knows what it
              opened and does not know what people opened alongside it, and a
              percentage computed from one side of that would get quoted.
            */}
            <p className="hint">
              A count, not a percentage: Reef knows what it opened and not what people opened
              alongside it, so the denominator would be invented.
            </p>
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Context graph</h2>
            {platform && <span className="chip">{platform.contextGraph.sources} sources</span>}
          </div>
          <div className="studio-card-body">
            <div className="tiles">
              <Tile
                label="Nodes"
                value={platform ? String(platform.contextGraph.nodes) : '—'}
                note="things Reef knows about"
              />
              <Tile
                label="Edges"
                value={platform ? String(platform.contextGraph.edges) : '—'}
                note="relationships between them"
              />
            </div>
            {platform && platform.contextGraph.byKind.length > 0 && (
              <p className="hint">
                {platform.contextGraph.byKind
                  .map((entry) => `${entry.count} ${entry.kind}`)
                  .join(' · ')}
              </p>
            )}
            {platform?.contextGraph.nodes === 0 && (
              <p className="hint">
                Empty. The code half fills on the next push; the workflow half on the next sweep.
              </p>
            )}
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Skills registry</h2>
            {platform && platform.skills.failingLint > 0 && (
              <span className="chip warn">{platform.skills.failingLint} failing lint</span>
            )}
          </div>
          <div className="studio-card-body">
            <div className="tiles">
              <Tile
                label="Installed"
                value={platform ? String(platform.skills.installed) : '—'}
                note="committed under /skills"
              />
              <Tile
                label="Runs"
                value={platform ? String(platform.skills.runs) : '—'}
                note="steps that mounted one"
              />
              <Tile
                label="Unused"
                value={platform ? String(platform.skills.unused) : '—'}
                note="nothing ran them in the window"
                tone={platform && platform.skills.unused > 0 ? 'warn' : undefined}
              />
              <Tile
                label="Failing lint"
                value={platform ? String(platform.skills.failingLint) : '—'}
                note="errors, not warnings"
                tone={platform && platform.skills.failingLint > 0 ? 'warn' : undefined}
              />
            </div>
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>MCP gateway</h2>
            {gateway && <span className="chip">threshold {gateway.threshold} tools</span>}
          </div>
          <div className="studio-card-body">
            <div className="tiles">
              <Tile
                label="Tools reachable"
                value={gateway ? String(gateway.tools) : '—'}
                note={`across ${gateway?.connections ?? 0} connections`}
              />
              <Tile
                label="Surface saved"
                value={gateway ? `${Math.round(gateway.savedShare * 100)}%` : '—'}
                note="per turn, versus declaring every tool"
              />
            </div>

            {gateway && gateway.byConnection.length > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>Connection</th>
                    <th>Tools</th>
                    <th>Declared</th>
                    <th>Discovered</th>
                  </tr>
                </thead>
                <tbody>
                  {gateway.byConnection.map((entry) => (
                    <tr key={entry.connectionId}>
                      <td>{entry.name}</td>
                      <td>{entry.tools}</td>
                      <td>{Math.round(entry.fullChars / 4)} tok</td>
                      <td>
                        {entry.discoveryChars === entry.fullChars
                          ? '—'
                          : `${Math.round(entry.discoveryChars / 4)} tok`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="hint">
              A dash means the mount is below the threshold and is declared in full — which is
              cheaper than discovering it.
            </p>
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>LLM gateway</h2>
            {llm && (
              <span className={`chip ${llm.within ? '' : 'warn'}`}>
                p95 {llm.stats.guardMsP95 ?? 0}ms of {llm.budgetMs}ms
              </span>
            )}
          </div>
          <div className="studio-card-body">
            <div className="tiles">
              <Tile
                label="Requests"
                value={llm ? String(llm.stats.requests) : '—'}
                note="model calls through the gateway"
              />
              <Tile
                label="Blocked"
                value={llm ? String(llm.stats.blocked) : '—'}
                note="never reached a provider"
                tone={llm && llm.stats.blocked > 0 ? 'warn' : undefined}
              />
              <Tile
                label="Redacted"
                value={llm ? String(llm.stats.redacted) : '—'}
                note="values stripped before dispatch"
              />
              <Tile
                label="Spend"
                value={llm ? `$${(llm.stats.spendCents / 100).toFixed(2)}` : '—'}
                note="attributed to a project and a team"
              />
            </div>

            {llm && llm.stats.byTeam && llm.stats.byTeam.length > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Requests</th>
                    <th>Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {llm.stats.byTeam.map((entry) => (
                    <tr key={entry.team}>
                      <td>{entry.team}</td>
                      <td>{entry.requests}</td>
                      <td>${(entry.spendCents / 100).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Warm environments</h2>
            {platform && <span className="chip">{platform.environments.ready} ready</span>}
          </div>
          <div className="studio-card-body">
            {pools.length === 0 ? (
              <p className="hint" style={{ marginTop: 0 }}>
                No pools configured. Container steps clone from scratch, which is correct and slow;
                a pool for a repository’s main ref makes them start from a prepared checkout.
              </p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Repository</th>
                    <th>Ref</th>
                    <th>Ready</th>
                    <th>Leased</th>
                    <th>Leases</th>
                    <th>Warm cost avoided</th>
                  </tr>
                </thead>
                <tbody>
                  {pools.map((pool) => (
                    <tr key={`${pool.repo}@${pool.ref}`}>
                      <td>{pool.repo}</td>
                      <td>{pool.ref}</td>
                      <td>
                        {pool.ready} / {pool.size}
                      </td>
                      <td>{pool.leased}</td>
                      <td>{pool.leases}</td>
                      <td>{pool.medianWarmMs === null ? '—' : `${Math.round(pool.medianWarmMs)}ms`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Assistant</h2>
          </div>
          <div className="studio-card-body">
            <div className="tiles">
              <Tile
                label="Sessions"
                value={platform ? String(platform.assistant.sessions) : '—'}
                note="questions answered in the window"
              />
              {(platform?.assistant.surfaces ?? []).map((surface) => (
                <Tile
                  key={surface.surface}
                  label={surface.surface}
                  value={String(surface.sessions)}
                  note="answered on this surface"
                />
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone?: 'warn';
}) {
  return (
    <div className={`tile ${tone ?? ''}`}>
      <p className="tile-label">{label}</p>
      <p className="tile-value">{value}</p>
      <p className="tile-note">{note}</p>
    </div>
  );
}
